/**
 * Supplier Portal access — shared constants and helpers.
 *
 * Sourcing and Supplier Portal are sold separately but share one `user` document,
 * so "same credentials" needs no token exchange: one registration sets one password
 * and each product is unlocked by its own node under `user.product_access`.
 *
 * Contract: src/assets/api_contract/cross_product_user_provisioning_api.md
 * Only §1-§4 and §8 apply to this app; §5-§7 belong to the Supplier Portal frontend.
 *
 *   §1  GET  /v1/products                       entitlement + both dropdowns
 *   §2  POST /v1/invite                         invite, optionally granting the portal
 *   §3  POST /v1/user/supplier_portal/access    grant on edit (grant-only)
 *   §4  GET  /v1/user/list                      rows carry a `supplier_portal` block
 *   §8  POST /v1/user/status/update             unchanged; scoped to Sourcing only
 */

/** Both on VUE_APP_API, which already includes /v1 — do not add it again. */
export const SP_PRODUCTS_ENDPOINT = '/products';
export const SP_GRANT_ENDPOINT = '/user/supplier_portal/access';

/**
 * The same Sourcing role is spelled three different ways in this codebase and there
 * is no single source of truth:
 *
 *   'procurement' — invite dropdown value  (AddUser.roleOption)
 *   'PRC'         — user object role code  (user.role / user.user_role)
 *   'SCM'         — edit dropdown id       (ManageUser.userRoles)
 *
 * Accept all three so callers never have to care which screen they are on. The
 * backend matches case- and space-insensitively too.
 */
const PRC_ALIASES = ['procurement', 'prc', 'scm'];

/**
 * Is this the Procurement role? Supplier Portal access is offered for PRC only.
 * @param {string} role
 * @returns {boolean}
 */
export function isPrcRole(role) {
    if (!role || typeof role !== 'string') return false;
    return PRC_ALIASES.indexOf(role.trim().toLowerCase()) !== -1;
}

/**
 * Has this tenant purchased Supplier Portal?
 * Read strictly from the GET /v1/products payload, which the backend derives from
 * `company.subscribed_products`. False until `seed_subscribed_products.py` has run
 * for the environment — no checkbox appears anywhere before that.
 * @param {object} products the `supplierPortal/getProducts` payload
 * @returns {boolean}
 */
export function isSupplierPortalPurchased(products) {
    return !!(products && products.supplier_portal && products.supplier_portal.purchased);
}

/** Value of `subscribed_products` that unlocks the Supplier Portal. */
export const SP_PRODUCT = 'supplier_portal';

/**
 * Does this session's tenant have the Supplier Portal in its product subscription?
 *
 * Login returns `subscribed_products` (e.g. `["sourcing", "supplier_portal"]`) on the
 * inner user object, and it is mirrored into localStorage['user'] and the shared
 * cross-product cookie, so no extra request is needed to gate navigation. The backend
 * derives it from `company.subscribed_products`, which older payloads nest instead —
 * both shapes are accepted.
 *
 * Defaults to FALSE when the field is absent: a stale session from before the backend
 * started sending it hides the entry rather than linking into a product the tenant may
 * not own. Re-login restores it. Distinct from {@link isSupplierPortalPurchased}, which
 * reads the GET /v1/products payload used by the user-provisioning screens.
 * @param {object} user the inner user object (NOT the {status, message, user} envelope)
 * @returns {boolean}
 */
export function hasSupplierPortalSubscription(user) {
    if (!user) return false;
    let products = user.product_access;
    if (!Array.isArray(products)) {
        products = user.company && user.company.product_access;
    }
    if (!Array.isArray(products)) return false;
    return products.some(
        (p) => typeof p === 'string' && p.trim().toLowerCase() === SP_PRODUCT
    );
}

/**
 * Is this user already in the Supplier Portal?
 *
 * Keys on `added` (the node existing), NOT `enabled` — per §4. A user deactivated
 * from the portal side keeps `added: true` and is deliberately not re-addable from
 * Sourcing.
 *
 * Defaults to FALSE when the block is absent, so a `/user/list` response from before
 * the backend deploys leaves the control usable rather than silently locking it.
 * @param {object} user a row from GET /v1/user/list
 * @returns {boolean}
 */
export function isAlreadyInSupplierPortal(user) {
    return !!(user && user.supplier_portal && user.supplier_portal.added);
}

/**
 * Is the user currently active in the portal? `false` after a portal-side
 * deactivation, which we surface as "inactive" rather than hiding.
 * @param {object} user a row from GET /v1/user/list
 * @returns {boolean}
 */
export function isPortalEnabled(user) {
    return !!(user && user.supplier_portal && user.supplier_portal.enabled);
}

/**
 * Map stored keys (`roles`, `access_level`) to their human labels using the option
 * arrays from GET /v1/products. The user list stores keys only, so display needs
 * this lookup. Falls back to the raw key when unmatched, so an option removed from
 * SP_CONFIG still renders something meaningful instead of blank.
 * @param {string[]} keys
 * @param {Array<{key: string, label: string}>} options
 * @returns {string} comma-joined labels, '' when there is nothing to show
 */
export function resolveLabels(keys, options) {
    if (!Array.isArray(keys) || !keys.length) return '';
    const list = Array.isArray(options) ? options : [];
    return keys
        .map((key) => {
            const match = list.find((o) => o && o.key === key);
            return (match && match.label) || key;
        })
        .join(', ');
}

/** Used when the host carries no `sourcing` label of its own (localhost, previews). */
const SP_FALLBACK_URL = 'https://develop.supplierportal.mintmesh.ai/';

/**
 * Where does the Supplier Portal live for the instance we are served from?
 *
 * The two products are deployed as sibling hosts under the same tenant label, so the
 * portal URL is the Sourcing URL with the `sourcing` label swapped for
 * `supplierportal` — `develop.sourcing.mintmesh.ai` pairs with
 * `develop.supplierportal.mintmesh.ai`, `qa.` with `qa.`, and so on. Deriving it beats
 * a per-environment env var: every new tenant host works without a rebuild.
 *
 * `VUE_APP_SUPPLIER_PORTAL_URL` still wins when set, which is how localhost (and any
 * host without a `sourcing` label) points somewhere other than the default.
 * @param {string} [hostname] defaults to the current host
 * @returns {string} absolute URL with a trailing slash
 */
export function supplierPortalUrl(hostname) {
    const override = process.env.VUE_APP_SUPPLIER_PORTAL_URL;
    if (override) return override;

    let host = hostname;
    if (host === undefined) {
        try {
            host = window.location.hostname;
        } catch (e) {
            host = '';
        }
    }

    const parts = String(host || '').split('.');
    const index = parts.indexOf('sourcing');
    if (index === -1) return SP_FALLBACK_URL;

    parts[index] = 'supplierportal';
    return `https://${parts.join('.')}/`;
}

/**
 * Build the optional `supplier_portal` block for POST /v1/invite (§2).
 *
 * Returns `{enabled: false}` when unticked — the contract accepts that or omission
 * entirely. `access_level` is an array on the wire; the UI is single-select, so the
 * chosen value is wrapped here rather than at the call site.
 * @param {boolean} enabled
 * @param {string} role one `key` from products.supplier_portal.roles
 * @param {string} accessLevel one `key` from products.supplier_portal.access_levels
 * @returns {object}
 */
export function buildInviteBlock(enabled, role, accessLevel) {
    if (!enabled) return { enabled: false };
    return {
        enabled: true,
        role,
        access_level: accessLevel ? [accessLevel] : [],
    };
}
